import React, { useEffect, useState } from "react";
import Template from "../utils/Template";
import Card from "../utils/Card";
import TableScroll from "../utils/TableScroll";
import { Table, TableBody, TableHeader } from "../utils/Table";
import { getMethod, postMethod, postMethodUpload } from "../service";
import {
  decodeData,
  errorToaster,
  successToaster,
  formatDate,
} from "../utils/Helper";
import { useNavigate } from "react-router-dom";
import ActionModal from "../utils/modals/ActionModal";
import { SETTINGS_API, SETTINGS_CUD_API } from "../service/constant";
import Switch from "../utils/Switch";

const Settings = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState({});
  const [formData, setFormData] = useState({});
  const [editId, setEditId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const navigate = useNavigate();

  const fetchSettings = async () => {
    setIsLoading(true);
    const token = decodeData(localStorage.getItem("r_c_a"));
    if (token) {
      const res = await getMethod(`${SETTINGS_API}`, token.token);
      if (res?.isSuccess) {
        setSettings(res.data);
        setFormData(res.data);
        setEditId(res.data._id);
      } else {
        errorToaster("Failed to fetch settings");
      }
      setIsLoading(false);
    } else {
      setIsLoading(false);
      errorToaster("Unauthenticated!");
      setTimeout(() => {
        navigate("/login");
      }, 100);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSwitchChange = (key, currentState) => {
    const newState = !currentState;
    updateProviderState(key, newState);
  };

  const updateProviderState = async (key, newState) => {
    setIsLoading(true);
    const token = decodeData(localStorage.getItem("r_c_a"));
    if (token) {
      try {
        const res = await postMethod(
          `${SETTINGS_CUD_API}/${editId}`,
          { [key]: newState },
          token.token
        );
        if (res?.isSuccess) {
          setSettings((prevSettings) => ({
            ...prevSettings,
            [key]: newState,
          }));
          successToaster(res?.message);
        } else {
          errorToaster(res?.message || "Something went wrong!");
        }
      } catch (error) {
        errorToaster("An error occurred");
      } finally {
        setIsLoading(false);
      }
    } else {
      errorToaster("Unauthenticated!");
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const token = decodeData(localStorage.getItem("r_c_a"));
    if (token) {
      try {
        const res = await postMethod(
          `${SETTINGS_CUD_API}/${editId}`,
          {
            odds: formData.odds,
            user_win_chance: formData?.user_win_chance,
            min_deposit: formData?.min_deposit,
            max_deposit: formData?.max_deposit,
            min_withdraw: formData?.min_withdraw,
            max_withdraw: formData?.max_withdraw,
          },
          token.token
        );
        if (res?.isSuccess) {
          successToaster("Settings updated successfully");
          setEditId(null);
          setShowEditModal(false);
          fetchSettings();
        } else {
          errorToaster("Failed to update settings");
        }
      } catch (error) {
        errorToaster("An error occurred");
      } finally {
        setIsLoading(false);
      }
    } else {
      errorToaster("Unauthenticated!");
    }
  };

  const handleEdit = () => {
    setFormData(settings);
    setEditId(settings._id);
    setShowEditModal(true);
  };

  return (
    <Template title={"Stock 2D - Admin Settings"} isLoading={isLoading}>
      <Card>
        <TableScroll>
          <Table>
            <TableHeader>
              <th>Odds</th>
              <th>Is Closed</th>
              <th>Deposit Status</th>
              <th>Min / Max Deposit</th>
              <th>Withdraw Status</th>
              <th>Min / Max Withdraw</th>
              <th>User Win Chance</th>
              <th>Updated At</th>
              <th>Actions</th>
            </TableHeader>
            <TableBody>
              {settings && (
                <tr style={{ verticalAlign: "middle" }}>
                  <td>{settings?.odds}</td>
                  <td>
                    <Switch
                      status={settings?.is_closed}
                      event={() =>
                        handleSwitchChange("is_closed", settings.is_closed)
                      }
                      id="is_closed"
                    />
                  </td>
                  <td>
                    <Switch
                      status={settings?.deposit_status}
                      event={() =>
                        handleSwitchChange(
                          "deposit_status",
                          settings.deposit_status
                        )
                      }
                      id="deposit_status"
                    />
                  </td>
                  <td>
                    {settings?.min_deposit?.toLocaleString()} /{" "}
                    {settings?.max_deposit?.toLocaleString()}
                  </td>
                  <td>
                    <Switch
                      status={settings?.withdraw_status}
                      event={(e) => {
                        handleSwitchChange(
                          "withdraw_status",
                          settings.withdraw_status
                        );
                      }}
                      id="withdraw_status"
                    />
                  </td>
                  <td>
                    {settings?.min_withdraw?.toLocaleString()} /{" "}
                    {settings?.max_withdraw?.toLocaleString()}
                  </td>

                  <td>{settings?.user_win_chance} %</td>
                  <td>{formatDate(settings?.updatedAt)}</td>
                  <td>
                    <button
                      className="btn btn-warning btn-sm py-1"
                      onClick={handleEdit}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      </Card>

      {showEditModal && (
        <ActionModal
          isLoading={isLoading}
          onSubmit={handleSubmit}
          onClose={() => setShowEditModal(false)}
          name="Edit Settings"
        >
          <form onSubmit={handleSubmit}>
            <label htmlFor="">Odds</label>
            <input
              name="odds"
              value={formData.odds || ""}
              onChange={handleInputChange}
              placeholder="Enter Odds"
              className="form-control mb-2"
              required
            />

            <label htmlFor="">Users Win Chance</label>
            <input
              name="user_win_chance"
              type="number"
              value={formData.user_win_chance || ""}
              onChange={handleInputChange}
              placeholder="Enter User Win Chance (%)"
              className="form-control mb-2"
              required
            />

            <label htmlFor="">Users Minimum Deposit</label>
            <input
              name="min_deposit"
              type="number"
              value={formData.min_deposit || ""}
              onChange={handleInputChange}
              placeholder="Enter Minimum Deposit"
              className="form-control mb-2"
              required
            />

            <label htmlFor="">Users Maximum Deposit</label>
            <input
              name="max_deposit"
              type="number"
              value={formData.max_deposit || ""}
              onChange={handleInputChange}
              placeholder="Enter Maximum Deposit"
              className="form-control mb-2"
              required
            />

            <label htmlFor="">Users Minimum Withdraw</label>
            <input
              name="min_withdraw"
              type="number"
              value={formData.min_withdraw || ""}
              onChange={handleInputChange}
              placeholder="Enter Minimum Withdraw"
              className="form-control mb-2"
              required
            />

            <label htmlFor="">Users Maximum Withdraw</label>
            <input
              name="max_withdraw"
              type="number"
              value={formData.max_withdraw || ""}
              onChange={handleInputChange}
              placeholder="Enter Maximum Withdraw"
              className="form-control mb-2"
              required
            />
          </form>
        </ActionModal>
      )}
    </Template>
  );
};

export default Settings;
